import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useNotificationStore } from '../stores/notificationStore';
import { useNotificationStream } from '../hooks/useNotificationStream';

/**
 * Section 28b / J2.10 — notification bell with an unread-count badge.
 *
 * M7 moved the centre off pure polling onto a push transport (SSE). The badge now updates the
 * moment a notification is created rather than up to a minute later, which is what makes it
 * usable for the things this milestone pushes: a supplier over-delivery the PURCHASER should act
 * on now, a PO that has just failed its third retry, a stock alert crossing CRITICAL.
 *
 * Polling is NOT removed — it is slowed down, and only while the stream is actually live:
 *
 *   • streaming → a 5-minute safety poll. It reconciles anything a dropped-and-reconnected
 *     EventSource missed in the gap, and it costs almost nothing.
 *   • not streaming → the original 60 s cadence, unchanged. A browser without EventSource, a
 *     proxy that buffers SSE, or a backend whose listener could not attach all land here, and
 *     the bell behaves exactly as it did before this milestone.
 *
 * The push is a hint to re-read the count, not the count itself: authorisation and the
 * recipient-scoping rules live in the API, and the badge must never display a number the caller
 * would not get from GET /notifications/unread-count.
 */
/** The unread count, kept fresh (push + safety poll). Shared by the bell and the menu entry. */
export function useUnreadNotifications(enabled = true): number {
  const count = useNotificationStore((s) => s.unread);
  const refresh = useNotificationStore((s) => s.refresh);

  const onPush = useCallback(() => {
    void refresh();
  }, [refresh]);
  const { streaming } = useNotificationStream(onPush, enabled);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const everyMs = streaming ? 300_000 : 60_000;
    const timer = setInterval(() => void refresh(), everyMs);
    return () => clearInterval(timer);
  }, [refresh, streaming, enabled]);
  return count;
}

export function NotificationBell({ size = 16 }: { size?: number } = {}) {
  const { t } = useTranslation();
  const count = useUnreadNotifications();

  const label =
    count > 0 ? t('components.notificationsWithCount', { count }) : t('notifications.title');

  return (
    <Link to="/notifications" title={label} aria-label={label} className="iconbutton relative">
      <Bell size={size} />
      {count > 0 && (
        <span
          aria-hidden="true"
          className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 text-center font-semibold"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}
